import { FC, useCallback } from "react";
import { DatasetLinkedOutlined } from "@mui/icons-material";
import { PopoverProps } from "@mui/material";
import { SourceDataChip } from "@/components/pages/dashboard/components/widgets/common/widget-header/source-and-name/SourceDataChip";
import { commitmentTypeTitleByType } from "@/components/pages/dashboard/components/adding-widget/widget-create/widget-setups/commitment-utilization/utils/constants/constants";
import {
  CommitmentCloudProvider,
  CommitmentUtilizationType,
} from "@/services/cloudchipr.api";
import { CommitmentUtilizationWidgetTypeSelect } from "@/components/pages/dashboard/components/widgets/commitment-utlization/components/CommitmentUtilizationWidgetTypeSelect";
import { WidgetQuickActionTrigger } from "@/components/pages/dashboard/components/adding-widget/widget-create/widget-setups/common/WidgetQuickActionTrigger";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateCommitmentUtilizationWidgetFromQuickActionsThunk } from "@/store/dashboards/thunks/widgets/commitment-utilization/actions/updateCommitmentUtilizationWidgetFromQuickActionsThunk";
import { useAppAbility } from "@/services/permissions";
import { currentDashboardProtectedSelector } from "@/store/dashboards/selectors/dashboard-hierarchy-visibility/currentDashboardProtectedSelector";

interface CommitmentUtilizationWidgetHeaderTypeSelectProps {
  widgetId?: string;
  providerOrganisationId?: string;
  provider?: CommitmentCloudProvider;
  commitmentType?: CommitmentUtilizationType;
}

export const CommitmentUtilizationWidgetHeaderTypeSelect: FC<
  CommitmentUtilizationWidgetHeaderTypeSelectProps
> = ({ commitmentType, providerOrganisationId, widgetId, provider }) => {
  const dispatch = useAppDispatch();
  const active = commitmentType === "all" ? "overview" : commitmentType;
  const commitmentTypes: CommitmentUtilizationType[] =
    commitmentType === "all" ? allOption : [commitmentType ?? "all"];
  const { cannot } = useAppAbility();
  const canNotEditWidget = cannot("edit", "dashboard");
  const isProtected = useAppSelector(currentDashboardProtectedSelector);

  const changeHandler = useCallback(
    (value: CommitmentUtilizationType[]) => {
      const newCommitmentType = value.length === 1 ? value.at(0) : "all";

      if (!newCommitmentType || newCommitmentType === commitmentType) {
        return;
      }

      dispatch(
        updateCommitmentUtilizationWidgetFromQuickActionsThunk({
          id: widgetId,
          commitment_type: newCommitmentType,
        }),
      );
    },
    [dispatch, widgetId, commitmentType],
  );

  return (
    <SourceDataChip
      url={`/commitments/${providerOrganisationId ?? provider}?active=${active}`}
      title={
        commitmentType === "all"
          ? "Commitments"
          : `${commitmentTypeTitleByType.get(commitmentType ?? "reservation")}s`
      }
      icon={DatasetLinkedOutlined}
      openIn="Commitments"
    >
      {widgetId && !canNotEditWidget && !isProtected && (
        <CommitmentUtilizationWidgetTypeSelect
          width={150}
          onChange={changeHandler}
          PopoverProps={popoverProps}
          commitmentTypes={commitmentTypes}
          CustomTriggerComponent={WidgetQuickActionTrigger}
        />
      )}
    </SourceDataChip>
  );
};

const allOption: CommitmentUtilizationType[] = ["savings_plan", "reservation"];

const popoverProps: Partial<PopoverProps> = {
  anchorOrigin: {
    vertical: "bottom",
    horizontal: "right",
  },
  transformOrigin: {
    vertical: "top",
    horizontal: "right",
  },
};
